import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { ChevronDown } from "lucide-react";
import { scrollToId } from "@/lib/lenis";

const EASE = [0.16, 1, 0.3, 1];

const LINES = [
  { text: "THE ULTIMATE", accent: false },
  { text: "VERSION OF YOU:", accent: false },
  { text: "UNLEASH FRICTIONLESS", accent: true },
  { text: "HIGH PERFORMANCE.", accent: true },
];

function VoltageGeometry({ mouseX, mouseY }) {
  const xBack = useTransform(mouseX, [-0.5, 0.5], [14, -14]);
  const yBack = useTransform(mouseY, [-0.5, 0.5], [10, -10]);
  const xMid = useTransform(mouseX, [-0.5, 0.5], [26, -26]);
  const yMid = useTransform(mouseY, [-0.5, 0.5], [20, -20]);
  const xFront = useTransform(mouseX, [-0.5, 0.5], [44, -44]);
  const yFront = useTransform(mouseY, [-0.5, 0.5], [34, -34]);

  return (
    <div className="relative w-full h-full" data-testid="hero-voltage-visual">
      <motion.svg
        style={{ x: xBack, y: yBack }}
        viewBox="0 0 600 600"
        className="absolute inset-0 w-full h-full opacity-40"
        fill="none"
      >
        {Array.from({ length: 9 }).map((_, i) => (
          <line
            key={`v-${i}`}
            x1={75 * (i + 1)}
            y1="0"
            x2={75 * (i + 1)}
            y2="600"
            stroke="#D4AF37"
            strokeOpacity="0.12"
            strokeWidth="1"
          />
        ))}
        {Array.from({ length: 9 }).map((_, i) => (
          <line
            key={`h-${i}`}
            x1="0"
            y1={75 * (i + 1)}
            x2="600"
            y2={75 * (i + 1)}
            stroke="#D4AF37"
            strokeOpacity="0.12"
            strokeWidth="1"
          />
        ))}
      </motion.svg>

      <motion.svg
        style={{ x: xMid, y: yMid }}
        viewBox="0 0 600 600"
        className="absolute inset-0 w-full h-full"
        fill="none"
      >
        <motion.g
          animate={{ rotate: 360 }}
          transition={{ duration: 90, repeat: Infinity, ease: "linear" }}
          style={{ originX: "300px", originY: "300px" }}
        >
          <circle cx="300" cy="300" r="230" stroke="#D4AF37" strokeOpacity="0.35" strokeWidth="1" strokeDasharray="3 9" />
        </motion.g>
        <motion.g
          animate={{ rotate: -360 }}
          transition={{ duration: 140, repeat: Infinity, ease: "linear" }}
          style={{ originX: "300px", originY: "300px" }}
        >
          <polygon
            points="300,90 482,195 482,405 300,510 118,405 118,195"
            stroke="#D4AF37"
            strokeOpacity="0.5"
            strokeWidth="1"
          />
          <polygon
            points="300,160 421,230 421,370 300,440 179,370 179,230"
            stroke="#B87333"
            strokeOpacity="0.55"
            strokeWidth="1"
          />
        </motion.g>
        <line x1="118" y1="405" x2="482" y2="195" stroke="#D4AF37" strokeOpacity="0.22" strokeWidth="1" />
        <line x1="118" y1="195" x2="482" y2="405" stroke="#D4AF37" strokeOpacity="0.22" strokeWidth="1" />
        <line x1="300" y1="90" x2="300" y2="510" stroke="#B87333" strokeOpacity="0.22" strokeWidth="1" />
      </motion.svg>

      <motion.svg
        style={{ x: xFront, y: yFront }}
        viewBox="0 0 600 600"
        className="absolute inset-0 w-full h-full"
        fill="none"
      >
        {[
          { cx: 300, cy: 90, d: "0s" },
          { cx: 482, cy: 195, d: "0.5s" },
          { cx: 482, cy: 405, d: "1s" },
          { cx: 300, cy: 510, d: "1.5s" },
          { cx: 118, cy: 405, d: "2s" },
          { cx: 118, cy: 195, d: "2.5s" },
          { cx: 300, cy: 300, d: "0.8s" },
        ].map((n, i) => (
          <g key={i}>
            <circle cx={n.cx} cy={n.cy} r="3.5" fill="#D4AF37" className="voltage-node" style={{ animationDelay: n.d }} />
            <circle cx={n.cx} cy={n.cy} r="9" stroke="#D4AF37" strokeOpacity="0.35" strokeWidth="1" className="voltage-node" style={{ animationDelay: n.d }} />
          </g>
        ))}
        <motion.circle
          cx="300"
          cy="300"
          r="70"
          stroke="#B87333"
          strokeOpacity="0.7"
          strokeWidth="1"
          animate={{ r: [70, 110, 70], strokeOpacity: [0.7, 0.1, 0.7] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.circle
          cx="300"
          cy="300"
          r="6"
          fill="#B87333"
          animate={{ scale: [1, 1.5, 1], opacity: [1, 0.6, 1] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          style={{ originX: "300px", originY: "300px" }}
        />
      </motion.svg>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const mouseX = useSpring(rawX, { stiffness: 40, damping: 20 });
  const mouseY = useSpring(rawY, { stiffness: 40, damping: 20 });

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const visualY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    rawX.set((e.clientX - r.left) / r.width - 0.5);
    rawY.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="hero"
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative min-h-screen flex items-center overflow-hidden bg-ink"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(184,115,51,0.10),transparent_55%)]" />
      <motion.div
        style={{ y: visualY }}
        className="absolute right-[-14%] sm:right-[-6%] lg:right-[2%] top-1/2 -translate-y-1/2 w-[88vw] sm:w-[62vw] lg:w-[46vw] max-w-[760px] aspect-square opacity-60 lg:opacity-90 pointer-events-none"
      >
        <VoltageGeometry mouseX={mouseX} mouseY={mouseY} />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 w-full pt-28 pb-20"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
          className="text-[11px] sm:text-xs uppercase tracking-[0.35em] text-copper-hover mb-6 sm:mb-8"
          data-testid="hero-eyebrow"
        >
          The Joe Fenix Method — Executive Somatic Engineering
        </motion.p>

        <h1
          className="font-serif font-medium text-[#D4AF37] tracking-tight leading-[1.06] text-4xl sm:text-6xl lg:text-7xl xl:text-[5.2rem] max-w-4xl"
          data-testid="hero-headline"
        >
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.accent ? "italic text-gold-soft" : ""}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.05, delay: 0.45 + i * 0.13, ease: EASE }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="overflow-hidden mt-7 sm:mt-9 max-w-xl">
          <motion.p
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay: 1.05, ease: EASE }}
            className="text-sm sm:text-base lg:text-lg text-neutral-400 font-light leading-relaxed"
            data-testid="hero-subtitle"
          >
            A transdisciplinary biological framework engineered for executives,
            leaders, and entrepreneurs to optimize mitochondrial energy and
            permanently crush burnout.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.35, ease: EASE }}
          className="mt-10 sm:mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        >
          <a
            data-testid="hero-cta-button"
            href="https://amazon.com/dp/B0HFD2BTGK"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative inline-block bg-copper hover:bg-copper-hover text-white font-semibold uppercase tracking-[0.22em] text-xs sm:text-sm px-9 sm:px-11 py-4 sm:py-5 transition-colors duration-300 shadow-[0_0_30px_rgba(184,115,51,0.30)] hover:shadow-[0_0_44px_rgba(184,115,51,0.5)] active:scale-[0.98]"
          >
            Acquire the Book
          </a>
          <button
            data-testid="hero-secondary-link"
            onClick={() => scrollToId("#commandments")}
            className="text-xs uppercase tracking-[0.25em] text-neutral-400 hover:text-gold transition-colors duration-300 border-b border-transparent hover:border-gold/50 pb-1"
          >
            Explore the 10 Commandments
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("#method")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 text-gold/60 hover:text-gold transition-colors"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
          <ChevronDown size={22} strokeWidth={1.25} />
        </motion.div>
      </motion.button>
    </section>
  );
}
